import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import API_BASE from '../config';
import {
  Banner, Loading, StatusBadge, FlowBadge, DetailRow, Modal,
  formatDateTime, formatDate, errorMessage,
} from '../components/ui';
import './ApplicationDetail.css';

const CAPTURE_FIELDS = [
  { key: 'NameThatWillBeUsed', label: 'Name on the certificate', required: true, wide: true },
  { key: 'InternalAccountCode', label: 'Internal account code', required: true },
  { key: 'Address1', label: 'Address 1', required: true },
  { key: 'Address2', label: 'Address 2' },
  { key: 'Address3', label: 'Address 3' },
  { key: 'Address4', label: 'Address 4' },
];

const STEPS = [
  { key: 'received', label: 'Received' },
  { key: 'captured', label: 'Captured' },
  { key: 'reviewed', label: 'Decided' },
  { key: 'sent', label: 'Sent' },
];

const CERT_STATE = {
  Valid: { cls: 'badge-green', label: 'Valid' },
  Expiring: { cls: 'badge-amber', label: 'Expiring soon' },
  Expired: { cls: 'badge-red', label: 'Expired' },
  Unknown: { cls: 'badge-grey', label: 'No validity dates' },
};

function ApplicationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Whoever opened this application says where "back" goes, so leaving the
  // Action Required queue returns to that queue rather than the full list.
  // A direct link or a page refresh has no such state, hence the fallback.
  const back = location.state?.from || { to: '/applications', label: 'All Applications' };

  const [app, setApp] = useState(null);
  const [commodities, setCommodities] = useState([]);
  const [allInspectors, setAllInspectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [warning, setWarning] = useState('');
  const [busy, setBusy] = useState('');

  const [capture, setCapture] = useState({});
  const [notes, setNotes] = useState('');
  const [selectedCommodityIds, setSelectedCommodityIds] = useState([]);
  const [selectedInspectorIds, setSelectedInspectorIds] = useState([]);
  const [flowOverride, setFlowOverride] = useState('');

  const [preview, setPreview] = useState(null);
  const [confirmSend, setConfirmSend] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [showRenew, setShowRenew] = useState(false);
  const [renewReason, setRenewReason] = useState('');
  const [showRaw, setShowRaw] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [appRes, commRes, inspRes] = await Promise.all([
        axios.get(`${API_BASE}/api/applications/${id}`),
        axios.get(`${API_BASE}/api/commodities`),
        axios.get(`${API_BASE}/api/inspectors`),
      ]);
      setApp(appRes.data);
      setCommodities(commRes.data);
      setAllInspectors(inspRes.data);
      setSelectedCommodityIds(appRes.data.commodities.map((c) => c.Id));
      setSelectedInspectorIds((appRes.data.inspectors || []).map((i) => i.Id));
      setNotes(appRes.data.DecisionNotes || '');
      setFlowOverride(appRes.data.FlowType && appRes.data.FlowType !== 'Denied' ? appRes.data.FlowType : '');
      const next = {};
      CAPTURE_FIELDS.forEach((f) => { next[f.key] = appRes.data[f.key] || ''; });
      setCapture(next);
    } catch (err) {
      setError(errorMessage(err, 'Could not load the application.'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="page-container"><div className="page-card"><Loading /></div></div>;
  }
  if (!app) {
    return (
      <div className="page-container"><div className="page-card">
        <Banner kind="error" message={error || 'Application not found.'} />
        <button className="btn btn-secondary" onClick={() => navigate(back.to)}>Back</button>
      </div></div>
    );
  }

  const isSent = app.Status === 'Sent';
  const missingRequired = CAPTURE_FIELDS
    .filter((f) => f.required && !String(capture[f.key] || '').trim())
    .map((f) => f.label);

  // Saved allocation, not the working selection — generating uses what is on
  // the record, so an unsaved tick must not unlock the button.
  const hasInspector = (app.inspectors || []).length > 0;
  const blockers = [
    ...missingRequired,
    ...(hasInspector ? [] : ['Inspector']),
  ];

  const liveCert = (app.certificates || []).find((c) => c.Status === 'Issued') || null;

  // A reopened renewal keeps its route but has the decision cleared, so the
  // decision — not the route — is what says the facility has been assessed.
  const hasDecision = app.IsCompliant === true || app.IsCompliant === false;

  const stepState = {
    received: true,
    captured: blockers.length === 0,
    reviewed: hasDecision,
    sent: isSent,
  };

  const canProduce = hasDecision && blockers.length === 0;

  const run = async (name, fn, msg) => {
    setBusy(name); setError(''); setSuccess(''); setWarning('');
    try {
      const r = await fn();
      if (msg) setSuccess(typeof msg === 'function' ? msg(r) : msg);
      await load();
      return r;
    } catch (err) { setError(errorMessage(err)); return null; } finally { setBusy(''); }
  };

  const saveAll = () =>
    run('save', async () => {
      await axios.patch(`${API_BASE}/api/applications/${id}`, { ...capture, DecisionNotes: notes });
      await axios.put(`${API_BASE}/api/applications/${id}/commodities`, { commodityIds: selectedCommodityIds });
      await axios.put(`${API_BASE}/api/applications/${id}/inspectors`, { inspectorIds: selectedInspectorIds });
    }, 'Saved.');

  const review = (isCompliant) =>
    run('review', async () => {
      await axios.patch(`${API_BASE}/api/applications/${id}`, { ...capture, DecisionNotes: notes });
      return axios.post(`${API_BASE}/api/applications/${id}/review`, {
        isCompliant,
        flowType: isCompliant ? (flowOverride || undefined) : undefined,
        decisionNotes: notes,
      });
    }, isCompliant ? 'Recorded as compliant.' : 'Recorded as non-compliant.');

  const openPreview = async () => {
    setBusy('preview'); setError('');
    try {
      const r = await axios.get(`${API_BASE}/api/applications/${id}/email-preview`);
      setPreview(r.data);
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(''); }
  };

  const testSend = () =>
    run('test', () => axios.post(`${API_BASE}/api/applications/${id}/test-send`), (r) => r?.data?.message);

  const sendToClient = async () => {
    setConfirmSend(false);
    await run('send', () => axios.post(`${API_BASE}/api/applications/${id}/send`), (r) => r?.data?.message);
  };

  const doRenew = async () => {
    await run('renew', () =>
      axios.post(`${API_BASE}/api/applications/${id}/renew`, { reason: renewReason }),
    (r) => r?.data?.message);
    setShowRenew(false);
    setRenewReason('');
  };

  const downloadPdf = (docId) => {
    const token = localStorage.getItem('token');
    fetch(`${API_BASE}/api/applications/${id}/documents/${docId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (!res.ok) throw new Error('Download failed.');
        const d = res.headers.get('content-disposition') || '';
        const m = d.match(/filename="?([^"]+)"?/i);
        return res.blob().then((blob) => ({ blob, name: m ? m[1] : 'document.pdf' }));
      })
      .then(({ blob, name }) => {
        const href = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = href; a.download = name;
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(href);
      })
      .catch((err) => setError(err.message));
  };

  /** Fetch one PDF and show it, keeping the rest of the set as tabs. */
  const showDoc = async (docs, index) => {
    const token = localStorage.getItem('token');
    const res = await fetch(
      `${API_BASE}/api/applications/${id}/documents/${docs[index].Id}?disposition=inline`,
      { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error('Could not open the document.');
    const url = window.URL.createObjectURL(await res.blob());
    setViewing((prev) => {
      if (prev?.url) window.URL.revokeObjectURL(prev.url);
      return { docs, index, url };
    });
  };

  /**
   * The only way documents are produced.
   *
   * There is no separate generate step: the server builds the set if it is
   * missing or has fallen behind an edit, so what is on screen is always what
   * the client would receive right now.
   */
  const openDocuments = async () => {
    setBusy('view'); setError(''); setSuccess(''); setWarning('');
    try {
      const r = await axios.post(`${API_BASE}/api/applications/${id}/documents`);
      const docs = r.data.documents || [];
      if (r.data.warnings?.length) setWarning(r.data.warnings.join(' '));
      if (r.data.regenerated) await load();
      if (!docs.length) { setError('No documents were produced for this application.'); return; }
      await showDoc(docs, 0);
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(''); }
  };

  const switchDoc = async (index) => {
    if (!viewing || index === viewing.index) return;
    try { await showDoc(viewing.docs, index); } catch (err) { setError(err.message); }
  };

  const viewFromList = async (index) => {
    setBusy(`view-${index}`); setError('');
    try { await showDoc(app.documents, index); }
    catch (err) { setError(err.message); } finally { setBusy(''); }
  };

  const closeViewer = () => {
    if (viewing?.url) window.URL.revokeObjectURL(viewing.url);
    setViewing(null);
  };

  const toggle = (setter) => (val) =>
    setter((prev) => (prev.includes(val) ? prev.filter((x) => x !== val) : [...prev, val]));

  return (
    <div className="page-container appd">
      {/* ============ Header ============ */}
      <div className="page-card appd-head">
        <div className="appd-head-main">
          <button className="btn btn-ghost btn-sm appd-back" onClick={() => navigate(back.to)}>
            &larr; {back.label}
          </button>
          <h2>{app.CompanyName || 'Unnamed applicant'}</h2>
          <div className="appd-facts">
            <span>{app.ApplicantEmail || 'no email'}</span>
            <span>·</span>
            <span>received {formatDate(app.CreatedAt)}</span>
            {app.InternalAccountCode && <><span>·</span><span className="mono">{app.InternalAccountCode}</span></>}
          </div>
        </div>
        <div className="appd-head-side">
          <StatusBadge status={app.Status} />
          <FlowBadge flowType={app.FlowType} />
        </div>

        <ol className="appd-steps">
          {STEPS.map((s, i) => (
            <li key={s.key} className={`appd-step${stepState[s.key] ? ' is-done' : ''}`}>
              <span className="appd-step-node">{stepState[s.key] ? '✓' : i + 1}</span>
              <span className="appd-step-label">{s.label}</span>
            </li>
          ))}
        </ol>
      </div>

      <Banner kind="success" message={success} onDismiss={() => setSuccess('')} />
      <Banner kind="error" message={error} onDismiss={() => setError('')} />
      <Banner kind="warning" message={warning} onDismiss={() => setWarning('')} />

      {/* ============ Certificate lifecycle ============ */}
      {liveCert && (
        <div className={`page-card appd-cert appd-cert-${liveCert.state.toLowerCase()}`}>
          <div className="appd-cert-main">
            <div className="appd-cert-head">
              <span className={`badge ${CERT_STATE[liveCert.state].cls}`}>{CERT_STATE[liveCert.state].label}</span>
              <strong>{liveCert.CertificateType === 'Renewal' ? 'Renewal certificate' : 'Certificate'} issued</strong>
            </div>
            <div className="appd-cert-dates">
              <span><em>Valid</em> {liveCert.IssueDateText || `${formatDate(liveCert.ValidFrom)} – ${formatDate(liveCert.ValidUntil)}`}</span>
              {liveCert.DaysRemaining !== null && (
                <span className={liveCert.state === 'Expired' ? 'is-red' : liveCert.state === 'Expiring' ? 'is-amber' : ''}>
                  {liveCert.DaysRemaining < 0
                    ? `expired ${Math.abs(liveCert.DaysRemaining)} days ago`
                    : `${liveCert.DaysRemaining} days remaining`}
                </span>
              )}
            </div>
            <p className="appd-cert-note">
              {liveCert.state === 'Expired' && (
                <><strong>Renewal is overdue.</strong> Re-assess the facility, then issue the
                renewal on this record — it will use the renewal templates and start a fresh
                validity period.</>
              )}
              {liveCert.state === 'Expiring' && (
                <><strong>Renewal is due.</strong> Confirm the facility is still compliant, then
                issue the renewal here. Managers subscribed to expiry notices are warned{' '}
                {liveCert.renewalWindowDays} days before lapse.</>
              )}
              {liveCert.state === 'Valid' && (
                <>Nothing to do until closer to expiry. When the time comes, use{' '}
                <strong>Issue renewal</strong> below to renew on this same record.</>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Previous cycles on this record */}
      {(app.certificates || []).filter((c) => c.Status !== 'Issued').length > 0 && (
        <div className="page-card appd-certhist">
          <h3 className="appd-pane-title">Previous certificates on this record</h3>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr><th className="nowrap">Type</th><th className="nowrap">Validity</th><th className="nowrap">Issued</th><th className="nowrap">By</th><th className="nowrap">Status</th></tr>
              </thead>
              <tbody>
                {app.certificates.filter((c) => c.Status !== 'Issued').map((c) => (
                  <tr key={c.Id}>
                    <td className="nowrap">{c.CertificateType}</td>
                    <td className="nowrap">{c.IssueDateText || `${formatDate(c.ValidFrom)} – ${formatDate(c.ValidUntil)}`}</td>
                    <td className="nowrap">{formatDate(c.IssuedAt)}</td>
                    <td>{c.IssuedBy}</td>
                    <td className="nowrap"><span className="badge badge-grey">{c.Status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ============ Working columns ============ */}
      <div className="appd-cols">

        {/* ---- Submission ---- */}
        <section className="page-card appd-pane">
          <h3>Submitted</h3>
          <div className="detail-rows appd-tight">
            <DetailRow label="Company" value={app.CompanyName} empty="—" />
            <DetailRow label="Contact" value={app.ApplicantName} empty="—" />
            <DetailRow label="Email" value={app.ApplicantEmail} empty="—" />
            <DetailRow label="Submitted" value={app.SheetTimestamp || formatDateTime(app.CreatedAt)} />
          </div>

          <h4 className="appd-sub">Applied for</h4>
          <p className="appd-raw-types">{app.CertificateTypesRaw || 'Nothing selected'}</p>

          <h4 className="appd-sub">Commodities <span className="appd-note">drives the certificate wording</span></h4>
          <div className="appd-checklist">
            {commodities.map((c) => (
              <label className="appd-check" key={c.Id}>
                <input type="checkbox" checked={selectedCommodityIds.includes(c.Id)}
                  onChange={() => toggle(setSelectedCommodityIds)(c.Id)} disabled={isSent} />
                <span>{c.Name}</span>
              </label>
            ))}
          </div>

          <h4 className="appd-sub">
            Inspector <span className={hasInspector ? 'appd-note' : 'appd-req'}>
              {hasInspector ? "CC'd on the outcome" : 'required — CC\'d on the outcome'}
            </span>
          </h4>
          <div className="appd-checklist appd-scroll">
            {allInspectors.length === 0
              ? <p className="appd-note">None configured.</p>
              : allInspectors.map((i) => (
                <label className="appd-check" key={i.Id}>
                  <input type="checkbox" checked={selectedInspectorIds.includes(i.Id)}
                    onChange={() => toggle(setSelectedInspectorIds)(i.Id)} disabled={isSent} />
                  <span>{i.Name || i.Email}</span>
                </label>
              ))}
          </div>

          {app.rawRow && (
            <>
              <button className="btn btn-ghost btn-sm appd-linkbtn" onClick={() => setShowRaw(!showRaw)}>
                {showRaw ? '− Hide' : '+ Show'} full form response
              </button>
              {showRaw && (
                <div className="appd-raw">
                  {Object.entries(app.rawRow).filter(([k]) => !k.startsWith('__')).map(([k, v]) => (
                    <div className="appd-raw-row" key={k}><span>{k}</span><span>{String(v) || '—'}</span></div>
                  ))}
                </div>
              )}
            </>
          )}
        </section>

        {/* ---- Capture ---- */}
        <section className="page-card appd-pane">
          <h3>Certificate details</h3>
          <p className="appd-note appd-note-block">
            These populate the certificate and letter. Confirm them before sending.
          </p>

          <div className="appd-fields">
            {CAPTURE_FIELDS.map((f) => (
              <div className={`appd-field${f.wide ? ' is-wide' : ''}`} key={f.key}>
                <label className={f.required ? 'field-required' : ''} htmlFor={`c-${f.key}`}>{f.label}</label>
                <input
                  id={`c-${f.key}`}
                  type="text"
                  value={capture[f.key] || ''}
                  onChange={(e) => setCapture({ ...capture, [f.key]: e.target.value })}
                  disabled={isSent}
                  className={f.required && !String(capture[f.key] || '').trim() ? 'is-invalid' : ''}
                />
              </div>
            ))}
            <div className="appd-field is-wide">
              <label htmlFor="c-notes">Reviewer notes</label>
              <textarea id="c-notes" rows={2} value={notes}
                onChange={(e) => setNotes(e.target.value)} disabled={isSent} />
            </div>
          </div>

          {!isSent && (
            <button className="btn btn-secondary btn-sm" onClick={saveAll} disabled={busy === 'save'}>
              {busy === 'save' ? 'Saving…' : 'Save details'}
            </button>
          )}
        </section>

        {/* ---- Decision ---- */}
        <section className="page-card appd-pane">
          <h3>Compliance decision</h3>

          {app.suggestion && !isSent && (
            <div className="appd-suggest">
              <strong>{app.suggestion.flowType}</strong> suggested — {app.suggestion.reason}
            </div>
          )}

          <div className="appd-choice" role="radiogroup" aria-label="Compliance decision">
            <button
              type="button"
              role="radio"
              aria-checked={app.IsCompliant === true}
              className={`appd-choice-btn is-yes${app.IsCompliant === true ? ' is-on' : ''}`}
              onClick={() => review(true)}
              disabled={isSent || busy === 'review'}
            >
              <span className="appd-choice-mark">{app.IsCompliant === true ? '●' : '○'}</span>
              Compliant
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={app.IsCompliant === false}
              className={`appd-choice-btn is-no${app.IsCompliant === false ? ' is-on' : ''}`}
              onClick={() => review(false)}
              disabled={isSent || busy === 'review'}
            >
              <span className="appd-choice-mark">{app.IsCompliant === false ? '●' : '○'}</span>
              Non-compliant
            </button>
          </div>

          <p className="appd-decision-state">
            {app.IsCompliant === null || app.IsCompliant === undefined ? (
              <span className="appd-note">No decision recorded yet.</span>
            ) : (
              <>
                Recorded as <strong>{app.IsCompliant ? 'Compliant' : 'Non-compliant'}</strong>
                {' → '}<FlowBadge flowType={app.FlowType} />
                {app.ReviewedBy && <span className="appd-note"> by {app.ReviewedBy}{app.ReviewedAt ? ` on ${formatDate(app.ReviewedAt)}` : ''}</span>}
              </>
            )}
          </p>

          {!isSent && app.IsCompliant !== false && (
            <div className="appd-field">
              <label htmlFor="flow-override">Route</label>
              <select id="flow-override" value={flowOverride} onChange={(e) => setFlowOverride(e.target.value)}>
                <option value="">Automatic — {app.suggestion?.flowType || 'New Issue'}</option>
                <option value="New Issue">Force New Issue</option>
                <option value="Renewal">Force Renewal</option>
              </select>
            </div>
          )}

          <h4 className="appd-sub">Documents &amp; delivery</h4>

          {!hasDecision ? (
            <p className="appd-blocked">Record the compliance decision first.</p>
          ) : blockers.length ? (
            <p className="appd-blocked">
              Required before sending: {blockers.join(', ')}
              {!hasInspector && (
                <span className="appd-blocked-hint">
                  Tick an inspector on the left, then <strong>Save details</strong>.
                </span>
              )}
            </p>
          ) : (
            <p className="appd-note appd-note-block">
              The documents are built from the details above when you view or send them, so
              they always match what is on this record.
            </p>
          )}

          <div className="appd-actions">
            <button className="btn btn-secondary btn-sm" onClick={openDocuments}
              disabled={!canProduce || busy === 'view'}>
              {busy === 'view' ? 'Preparing…' : 'View documents'}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={openPreview}
              disabled={!hasDecision || busy === 'preview'}>
              {busy === 'preview' ? '…' : 'Preview email'}
            </button>
            {!isSent && (
              <>
                <button className="btn btn-secondary btn-sm" onClick={testSend}
                  disabled={!canProduce || busy === 'test'}>
                  {busy === 'test' ? 'Sending…' : 'Test to myself'}
                </button>
                <button className="btn btn-primary btn-sm" onClick={() => setConfirmSend(true)}
                  disabled={!canProduce || busy === 'send'}>
                  {busy === 'send' ? 'Sending…' : 'Send to client'}
                </button>
              </>
            )}
            {isSent && liveCert && (
              <button
                className={`btn btn-sm ${liveCert.state === 'Valid' ? 'btn-secondary' : 'btn-primary'}`}
                onClick={() => { setShowRenew(true); setRenewReason(''); }}
              >
                Issue renewal…
              </button>
            )}
          </div>

          {isSent && (
            <p className="appd-sentline">
              Delivered to {app.ApplicantEmail} by {app.SentBy} on {formatDate(app.SentAt)}.
            </p>
          )}
        </section>
      </div>

      {/* ============ Documents ============ */}
      {app.documents.length > 0 && (
        <div className="page-card">
          <h3 className="appd-pane-title">Documents</h3>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr><th>Document</th><th className="nowrap">Type</th><th className="nowrap">Prepared</th><th className="nowrap">Actions</th></tr>
              </thead>
              <tbody>
                {app.documents.map((d, i) => (
                  <tr key={d.Id}>
                    <td>{d.FileName}</td>
                    <td className="nowrap"><span className="badge badge-grey">{d.Kind}</span></td>
                    <td className="nowrap">{formatDateTime(d.GeneratedAt)}</td>
                    <td className="nowrap">
                      <button className="btn btn-secondary btn-sm" onClick={() => viewFromList(i)}
                        disabled={busy === `view-${i}`}>
                        {busy === `view-${i}` ? '…' : 'View'}
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => downloadPdf(d.Id)}>Download</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ============ History ============ */}
      <div className="page-card">
        <button className="appd-history-toggle" onClick={() => setShowHistory(!showHistory)}>
          {showHistory ? '−' : '+'} History <span className="appd-note">({app.history.length} entries)</span>
        </button>
        {showHistory && (
          app.history.length === 0 ? <p className="appd-note">Nothing recorded.</p> : (
            <ul className="appd-history">
              {app.history.map((h) => (
                <li key={h.Id}>
                  <span className="appd-history-when">{formatDateTime(h.PerformedAt)}</span>
                  <span className="appd-history-what">
                    <strong>{h.Action}</strong>
                    {h.FieldName && <> · {h.FieldName}: <em>{h.OldValue || '(blank)'}</em> → <em>{h.NewValue || '(blank)'}</em></>}
                  </span>
                  <span className="appd-history-who">{h.PerformedBy}</span>
                </li>
              ))}
            </ul>
          )
        )}
      </div>

      {/* ============ Modals ============ */}
      {viewing && (
        <div className="appd-viewer-backdrop" onClick={closeViewer}>
          <div className="appd-viewer" onClick={(e) => e.stopPropagation()}>
            <div className="appd-viewer-head">
              <div>
                <h3>{viewing.docs[viewing.index].FileName}</h3>
                <span className="appd-viewer-sub">
                  {viewing.docs[viewing.index].Kind}
                  {viewing.docs.length > 1 && ` · ${viewing.index + 1} of ${viewing.docs.length}`}
                </span>
              </div>
              <div className="appd-viewer-actions">
                <button className="btn btn-secondary btn-sm"
                  onClick={() => downloadPdf(viewing.docs[viewing.index].Id)}>Download</button>
                <button className="btn btn-ghost btn-sm" onClick={closeViewer}>Close</button>
              </div>
            </div>
            {viewing.docs.length > 1 && (
              <div className="appd-viewer-tabs">
                {viewing.docs.map((d, i) => (
                  <button key={d.Id} type="button"
                    className={`appd-viewer-tab${i === viewing.index ? ' is-on' : ''}`}
                    onClick={() => switchDoc(i)}>
                    {d.Kind}
                  </button>
                ))}
              </div>
            )}
            {/* #view=Fit tells the browser's PDF viewer to size the page to the
                frame, rather than opening at a fixed zoom that leaves a
                landscape certificate too small to read. */}
            <iframe src={`${viewing.url}#view=Fit`} title={viewing.docs[viewing.index].FileName}
              className="appd-viewer-frame" />
          </div>
        </div>
      )}

      {preview && (
        <Modal title="Email preview" subtitle="Exactly what the client receives."
          onClose={() => setPreview(null)} wide>
          <div className="detail-rows appd-tight">
            <DetailRow label="From" value={preview.from} />
            <DetailRow label="To" value={preview.to} empty="No address" />
            <DetailRow label="CC" value={preview.cc} empty="None" />
            <DetailRow label="Subject" value={preview.subject} />
            <DetailRow label="Attachments" value={preview.attachments.join(', ')} empty="None" />
          </div>
          <div className="appd-preview-body" dangerouslySetInnerHTML={{ __html: preview.body }} />
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setPreview(null)}>Close</button>
          </div>
        </Modal>
      )}

      {confirmSend && (
        <Modal title="Send to the client?" subtitle="This emails the applicant and cannot be undone."
          onClose={() => setConfirmSend(false)}>
          <div className="detail-rows appd-tight">
            <DetailRow label="Recipient" value={app.ApplicantEmail} empty="No address" />
            <DetailRow label="Copied" value={(app.inspectors || []).map((i) => i.Email).join(', ')} empty="No inspector" />
            <DetailRow label="Route" value={app.FlowType} />
            <DetailRow
              label="Attachments"
              value={app.documents.map((d) => d.FileName).join(', ')}
              empty="Built from this record as it sends"
            />
          </div>
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setConfirmSend(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={sendToClient} disabled={!app.ApplicantEmail}>Send now</button>
          </div>
        </Modal>
      )}

      {showRenew && (
        <Modal
          title="Issue a renewal"
          subtitle="Re-assess the facility and issue a fresh certificate on this record."
          onClose={() => setShowRenew(false)}
        >
          <div className="callout callout-tip">
            <span className="callout-label">What this does</span>
            Reopens this application as a <strong>Renewal</strong> and clears the compliance
            decision so the facility is assessed again. The renewal templates are used and the
            validity period starts fresh from the day you send. The current certificate is
            retired once the new one goes out.
          </div>
          <div className="field">
            <label className="field-required" htmlFor="renew-reason">Reason</label>
            <textarea id="renew-reason" rows={3} value={renewReason}
              onChange={(e) => setRenewReason(e.target.value)}
              placeholder="e.g. Six-month period elapsed; re-inspected and still compliant." />
            <span className="field-hint">Recorded on the application and in the activity log.</span>
          </div>
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setShowRenew(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={doRenew}
              disabled={!renewReason.trim() || busy === 'renew'}>
              {busy === 'renew' ? 'Reopening…' : 'Start renewal'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default ApplicationDetail;
